import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import UserManagement from './UserManagement';
import PrepareForNewYear from './PrepareForNewYear';
import './AdminSettings.css';
import { sendTestEmail } from '../../utils/sendEmail';

interface AccommodationRate {
  weekly: number;
  nightly: number;
}

// Define the structure for each accommodation type's rates
interface CottageRates {
  C1: AccommodationRate;
  C2: AccommodationRate;
  C3: AccommodationRate;
  P1: AccommodationRate;
  P2: AccommodationRate;
  P3: AccommodationRate;
}

interface ApartmentRates {
  A1: AccommodationRate;
  A2: AccommodationRate;
  A3: AccommodationRate;
}

interface DormRates {
  D1: AccommodationRate;
  D2: AccommodationRate;
}

interface CampsiteRates {
  T1: AccommodationRate;
  T2: AccommodationRate;
}

interface MealRates {
  fullCampAdult: number;
  fullCampChild: number;
  dailyAdult: number;
  dailyChild: number;
}

interface CampRates {
  registration: {
    adult: AccommodationRate;
    adultStudent: AccommodationRate;
    youthAndChildren: AccommodationRate;
  };
  accommodation: {
    cottages: CottageRates;
    apartments: ApartmentRates;
    dorm: DormRates;
    campsite: CampsiteRates;
  };
  meals: MealRates;
}

interface ProgramParagraphSetting {
  enabled: boolean;
  text: string;
}

interface ConfirmationEmailProgramParagraphs {
  nursery: ProgramParagraphSetting;
  vbc: ProgramParagraphSetting;
  seniorYouth: ProgramParagraphSetting;
}

interface CampSettings {
  startDate: string;
  endDate: string;
  rates: CampRates;
  year: number;
  welcomeMessage: string;
  acceptingRegistrations: boolean;
  mailerSend: {
    enabled: boolean;
    fromEmail: string;
    fromName: string;
  };
  confirmationEmailProgramParagraphs: ConfirmationEmailProgramParagraphs;
}

// Define valid accommodation types for rates
type RateAccommodationType = 'cottages' | 'apartments' | 'dorm' | 'campsite';

// Define all accommodation types including display-only types
type AccommodationType = RateAccommodationType | 'Fireside' | 'UpperCourt';

type RegistrationType = keyof CampRates['registration'];

const calculateNightlyRate = (weeklyRate: number): number => {
  return Math.round(weeklyRate * 0.2);
};

const formatCurrency = (amount: number) => {
  return `$${amount.toFixed(2)}`;
};

const stripCurrency = (value: string): string => {
  return value.replace(/[^0-9.]/g, '');
};

// Add this after the other utility functions
const getAccommodationDescription = (type: string, code: string): string => {
  const descriptions: { [key: string]: { [key: string]: string } } = {
    cottages: {
      C1: 'Cottage Full Service (C1)',
      C2: 'Cottage Partial Service (C2)',
      C3: 'Cottage No Service (C3)',
      P1: 'Private Cottage Full Service (P1)',
      P2: 'Private Cottage Partial Service (P2)',
      P3: 'Private Cottage No Service (P3)'
    },
    apartments: {
      A1: 'Apartment Full Service (A1)',
      A2: 'Apartment Partial Service (A2)',
      A3: 'Apartment No Service (A3)'
    },
    dorm: {
      D1: 'Dorm/Fireside (Single D1)',
      D2: 'Dorm/Fireside (Double D2)'
    },
    campsite: {
      T1: 'Camp Site (Full Service T1)',
      T2: 'Camp Site (Partial Service T2)'
    }
  };

  return descriptions[type]?.[code] || code;
};

// Add this sorting function after getAccommodationDescription
const getSortedAccommodationKeys = (type: string, keys: string[]): string[] => {
  const sortOrder: { [key: string]: { [key: string]: number } } = {
    cottages: {
      C1: 1, C2: 2, C3: 3,
      P1: 4, P2: 5, P3: 6
    },
    apartments: {
      A1: 1, A2: 2, A3: 3
    },
    dorm: {
      D1: 1, D2: 2
    },
    campsite: {
      T1: 1, T2: 2
    }
  };

  return [...keys].sort((a, b) => {
    return (sortOrder[type]?.[a] || 0) - (sortOrder[type]?.[b] || 0);
  });
};

// Add this utility function near the other utility functions
const formatDateWithDay = (dateStr: string): string => {
  if (!dateStr) return '';
  // Add T12:00:00 to force noon in local timezone
  const date = new Date(dateStr + 'T12:00:00');
  const day = date.toLocaleDateString('en-US', { weekday: 'long' });
  const formattedDate = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  return `${day} ${formattedDate}`;
};

const isLinkText = (value: string): boolean => {
  return /^(https?:\/\/\S+|www\.\S+)$/i.test(value);
};

const toClickableUrl = (value: string): string => {
  return value.toLowerCase().startsWith('www.') ? `https://${value}` : value;
};

const renderParagraphPreviewWithLinks = (text: string): React.ReactNode => {
  if (!text) return null;

  const linkPattern = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  const lines = text.split(/\r?\n/);

  return lines.map((line, lineIndex) => {
    const parts = line.split(linkPattern);
    return (
      <React.Fragment key={`line-${lineIndex}`}>
        {parts.map((part, partIndex) => {
          if (!part) return null;
          if (isLinkText(part)) {
            const href = toClickableUrl(part);
            return (
              <a
                key={`part-${lineIndex}-${partIndex}`}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {part}
              </a>
            );
          }
          return <React.Fragment key={`part-${lineIndex}-${partIndex}`}>{part}</React.Fragment>;
        })}
        {lineIndex < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
};

const DEFAULT_CONFIRMATION_EMAIL_PROGRAM_PARAGRAPHS: ConfirmationEmailProgramParagraphs = {
  nursery: { enabled: false, text: '' },
  vbc: { enabled: false, text: '' },
  seniorYouth: { enabled: false, text: '' }
};

const AdminSettings: React.FC = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [isEditingDates, setIsEditingDates] = useState(false);
  const [tempDates, setTempDates] = useState({ startDate: '', endDate: '' });

  const [settings, setSettings] = useState<CampSettings>({
    year: new Date().getFullYear(),
    startDate: '',
    endDate: '',
    welcomeMessage: 'Welcome to Berwick Camp Registration!',
    acceptingRegistrations: false,
    mailerSend: {
      enabled: false,
      fromEmail: '',
      fromName: 'Berwick Camp'
    },
    confirmationEmailProgramParagraphs: DEFAULT_CONFIRMATION_EMAIL_PROGRAM_PARAGRAPHS,
    rates: {
      registration: {
        adult: { weekly: 180, nightly: 36 },
        adultStudent: { weekly: 90, nightly: 18 },
        youthAndChildren: { weekly: 90, nightly: 18 }
      },
      accommodation: {
        cottages: {
          C1: { weekly: 0, nightly: 0 },
          C2: { weekly: 0, nightly: 0 },
          C3: { weekly: 0, nightly: 0 },
          P1: { weekly: 0, nightly: 0 },
          P2: { weekly: 0, nightly: 0 },
          P3: { weekly: 0, nightly: 0 }
        },
        apartments: {
          A1: { weekly: 0, nightly: 0 },
          A2: { weekly: 0, nightly: 0 },
          A3: { weekly: 0, nightly: 0 }
        },
        dorm: {
          D1: { weekly: 0, nightly: 0 },
          D2: { weekly: 0, nightly: 0 }
        },
        campsite: {
          T1: { weekly: 0, nightly: 0 },
          T2: { weekly: 0, nightly: 0 }
        }
      },
      meals: {
        fullCampAdult: 100,
        fullCampChild: 50,
        dailyAdult: 15,
        dailyChild: 10
      }
    }
  });

    const fetchSettings = async () => {
      try {
      setLoading(true);
        const settingsRef = doc(db, 'settings', 'camp');
      const docSnap = await getDoc(settingsRef);
      const data = docSnap.data();
      
      const newSettings = {
        year: data?.year || new Date().getFullYear(),
        startDate: data?.startDate || '',
        endDate: data?.endDate || '',
        welcomeMessage: data?.welcomeMessage || 'Welcome to Berwick Camp Registration!',
        acceptingRegistrations: data?.acceptingRegistrations || false,
        mailerSend: data?.mailerSend || settings.mailerSend,
        confirmationEmailProgramParagraphs: {
          nursery: {
            enabled: !!data?.confirmationEmailProgramParagraphs?.nursery?.enabled,
            text: data?.confirmationEmailProgramParagraphs?.nursery?.text || ''
          },
          vbc: {
            enabled: !!data?.confirmationEmailProgramParagraphs?.vbc?.enabled,
            text: data?.confirmationEmailProgramParagraphs?.vbc?.text || ''
          },
          seniorYouth: {
            enabled: !!data?.confirmationEmailProgramParagraphs?.seniorYouth?.enabled,
            text: data?.confirmationEmailProgramParagraphs?.seniorYouth?.text || ''
          }
        },
        rates: {
          registration: {
            adult: {
              weekly: data?.rates?.registration?.adult?.weekly ?? 180,
              nightly: data?.rates?.registration?.adult?.nightly ?? 36
            },
            adultStudent: {
              weekly: data?.rates?.registration?.adultStudent?.weekly ?? 90,
              nightly: data?.rates?.registration?.adultStudent?.nightly ?? 18
            },
            youthAndChildren: {
              weekly: data?.rates?.registration?.youthAndChildren?.weekly ?? 90,
              nightly: data?.rates?.registration?.youthAndChildren?.nightly ?? 18
            }
          },
          accommodation: data?.rates?.accommodation || settings.rates.accommodation,
          meals: {
            fullCampAdult: data?.rates?.meals?.fullCampAdult ?? 100,
            fullCampChild: data?.rates?.meals?.fullCampChild ?? 50,
            dailyAdult: data?.rates?.meals?.dailyAdult ?? 15,
            dailyChild: data?.rates?.meals?.dailyChild ?? 10
          }
        }
      };

      if (!docSnap.exists() || !data?.rates?.registration) {
        await setDoc(settingsRef, {
          ...newSettings,
          lastModified: new Date().toISOString(),
          modifiedBy: currentUser?.uid || 'system'
        });
      }
      
      setSettings(newSettings);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
      if (!currentUser) {
        setError('You must be logged in to save settings');
        return;
      }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      console.log('Attempting to save settings:', {
        uid: currentUser.uid,
        settings
      });

      const settingsRef = doc(db, 'settings', 'camp');
      await setDoc(settingsRef, {
        ...settings,
        lastModified: new Date().toISOString(),
        modifiedBy: currentUser.uid
      });
      
      setSuccess('Settings saved successfully');
      setHasUnsavedChanges(false);
    } catch (err: any) {
      console.error('Error saving settings:', err);
      setError(`Failed to save settings: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newYear = parseInt(e.target.value);
    const { startDate, endDate } = calculateDefaultDates(newYear);
    
    // Cancel any date editing in progress
    setIsEditingDates(false);
    
    // Update settings with new year and calculated dates
    setSettings(prev => ({
      ...prev,
      year: newYear,
      startDate,
      endDate
    }));
    
    // Also update tempDates to ensure consistency
    setTempDates({ startDate, endDate });
    
    setHasUnsavedChanges(true);
  };

  const handleWeeklyRateChange = (
    category: 'registration' | 'accommodation',
    type: AccommodationType | RegistrationType,
    subType: string | null,
    value: string
  ) => {
    setEditValue(value);
  };

  const handleFocus = (
    category: 'registration' | 'accommodation',
    type: AccommodationType | RegistrationType,
    subType: string | null,
    currentValue: number
  ) => {
    const fieldId = getFieldId(category, type as string, subType);
    setEditingField(fieldId);
    setEditValue(currentValue.toString());
  };

  const handleBlur = (
    category: 'registration' | 'accommodation',
    type: AccommodationType | RegistrationType,
    subType: string | null
  ) => {
    const numericValue = parseFloat(stripCurrency(editValue));
    if (!isNaN(numericValue)) {
      setSettings(prev => {
        const newSettings = JSON.parse(JSON.stringify(prev));
        if (category === 'registration') {
          const regType = type as RegistrationType;
          newSettings.rates.registration[regType] = {
            weekly: numericValue,
            nightly: calculateNightlyRate(numericValue)
          };
        } else {
          if (subType) {
            const accType = type as AccommodationType;
            const rateType = (
              accType === 'UpperCourt' ? 'campsite' :
              accType === 'Fireside' ? 'dorm' :
              accType
            ) as RateAccommodationType;
            
            if (rateType === 'cottages' && (subType in newSettings.rates.accommodation.cottages)) {
              newSettings.rates.accommodation.cottages[subType as keyof CottageRates] = {
                weekly: numericValue,
                nightly: calculateNightlyRate(numericValue)
              };
            } else if (rateType === 'apartments' && (subType in newSettings.rates.accommodation.apartments)) {
              newSettings.rates.accommodation.apartments[subType as keyof ApartmentRates] = {
                weekly: numericValue,
                nightly: calculateNightlyRate(numericValue)
              };
            } else if (rateType === 'dorm' && (subType in newSettings.rates.accommodation.dorm)) {
              newSettings.rates.accommodation.dorm[subType as keyof DormRates] = {
                weekly: numericValue,
                nightly: calculateNightlyRate(numericValue)
              };
            } else if (rateType === 'campsite' && (subType in newSettings.rates.accommodation.campsite)) {
              newSettings.rates.accommodation.campsite[subType as keyof CampsiteRates] = {
                weekly: numericValue,
                nightly: calculateNightlyRate(numericValue)
              };
            }
          }
        }
        return newSettings;
      });
      setHasUnsavedChanges(true);
    }
    setEditingField(null);
    setEditValue('');
  };

  // Helper function to generate a unique field ID
  const getFieldId = (category: string, type: string, subType: string | null) => {
    return `${category}-${type}-${subType || 'null'}`;
  };

  const handleResetAccommodations = async () => {
    try {
      setResetLoading(true);
      setResetError('');
      
      const inventoryRef = doc(db, 'inventory', 'accommodations');
      const inventoryDoc = await getDoc(inventoryRef);
      
      if (inventoryDoc.exists()) {
        const inventoryData = inventoryDoc.data();
        
        // Reset status to 'available' for all accommodation types
        const resetAccommodations = (accommodations: any[]) => {
          return accommodations.map(acc => ({
            ...acc,
            status: 'available'
          }));
        };

        const updatedInventory = {
          cottages: resetAccommodations(inventoryData.cottages || []),
          apartments: resetAccommodations(inventoryData.apartments || []),
          dorm: resetAccommodations(inventoryData.dorm || []),
          Fireside: resetAccommodations(inventoryData.Fireside || []),
          campsite: resetAccommodations(inventoryData.campsite || [])
        };

        await setDoc(inventoryRef, updatedInventory);
        setResetSuccess(true);
        setShowResetModal(false);
      }
    } catch (error) {
      console.error('Error resetting accommodations:', error);
      setResetError('Failed to reset accommodations. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  // Calculate default last Friday of July dates
  const calculateDefaultDates = (year: number) => {
    // Start with July 31st
    const date = new Date(year, 6, 31);
    
    // Back up to the last Friday (if not already Friday)
    while (date.getDay() !== 5) { // 5 = Friday
      date.setDate(date.getDate() - 1);
    }
    
    // Now we have the last Friday
    const lastFriday = new Date(date);
    
    // Calculate end date (10 days from start, including start day)
    const endDate = new Date(lastFriday);
    endDate.setDate(lastFriday.getDate() + 9);

    // For debugging
    console.log('Last Friday of July ' + year + ':', lastFriday.toISOString().split('T')[0]);
    console.log('End Date:', endDate.toISOString().split('T')[0]);

    return {
      startDate: lastFriday.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  const handleTestEmail = async () => {
    if (!testEmailAddress) {
      setError('Please enter an email address');
      return;
    }

    try {
      setSendingTestEmail(true);
      setError(null);
      await sendTestEmail(testEmailAddress);
      setEmailSuccess('Test email sent successfully!');
      setTestEmailAddress('');
    } catch (err: any) {
      setError(err.message || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  // Keep the beforeunload handler to warn about unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    fetchSettings();
  }, [currentUser]);

  // Sync tempDates with settings whenever settings dates change
  useEffect(() => {
    setTempDates({
      startDate: settings.startDate,
      endDate: settings.endDate
    });
  }, [settings.startDate, settings.endDate]);

  // Add these handlers near the other handler functions
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSettings(prev => ({
      ...prev,
      startDate: e.target.value
    }));
    setHasUnsavedChanges(true);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSettings(prev => ({
      ...prev,
      endDate: e.target.value
    }));
    setHasUnsavedChanges(true);
  };

  const handleEditDates = () => {
    setTempDates({
      startDate: settings.startDate,
      endDate: settings.endDate
    });
    setIsEditingDates(true);
  };

  const handleSaveDates = () => {
    setSettings(prev => ({
      ...prev,
      startDate: tempDates.startDate,
      endDate: tempDates.endDate
    }));
    setIsEditingDates(false);
    setHasUnsavedChanges(true);
  };

  const handleCancelEdit = () => {
    setIsEditingDates(false);
    setTempDates({ startDate: '', endDate: '' });
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="admin-settings">
      <h1>Annual Settings</h1>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      {emailSuccess && <div className="success-message">{emailSuccess}</div>}

      <div className="settings-section">
        <h2>Registration Status</h2>
        <div className="form-row">
          <div className="form-group">
            <label className="toggle-switch-label">
              <span>Accepting Registrations</span>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.acceptingRegistrations}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      acceptingRegistrations: e.target.checked
                    }));
                    setHasUnsavedChanges(true);
                  }}
                />
                <span className="toggle-slider"></span>
              </div>
              <span className="toggle-status">
                {settings.acceptingRegistrations ? 'Yes' : 'No'}
              </span>
            </label>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h2>Camp Year and Dates</h2>
        <div className="year-row">
          <div className="year-label">Camp Year</div>
          <select
            className="year-select"
            value={settings.year}
            onChange={handleYearChange}
          >
            {Array.from({ length: 10 }, (_, i) => i + 2024).map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
        <div className="dates-row">
          <div className="dates-content">
            <div className="date-group">
              <div className="date-label">Start Date</div>
              <input
                type="date"
                className={`date-input ${isEditingDates ? 'editing' : ''}`}
                value={isEditingDates ? tempDates.startDate : settings.startDate}
                onChange={(e) => setTempDates(prev => ({ ...prev, startDate: e.target.value }))}
                disabled={!isEditingDates}
              />
              <div className="date-display" key={`start-${settings.startDate}`}>
                {formatDateWithDay(isEditingDates ? tempDates.startDate : settings.startDate)}
              </div>
            </div>
            <div className="date-group">
              <div className="date-label">End Date</div>
              <input
                type="date"
                className={`date-input ${isEditingDates ? 'editing' : ''}`}
                value={isEditingDates ? tempDates.endDate : settings.endDate}
                onChange={(e) => setTempDates(prev => ({ ...prev, endDate: e.target.value }))}
                disabled={!isEditingDates}
              />
              <div className="date-display" key={`end-${settings.endDate}`}>
                {formatDateWithDay(isEditingDates ? tempDates.endDate : settings.endDate)}
              </div>
            </div>
          </div>
          <div className="dates-actions">
            {!isEditingDates ? (
              <button className="edit-dates-button" onClick={handleEditDates}>
                Edit Dates
              </button>
            ) : (
              <div className="edit-actions">
                <button className="save-dates-button" onClick={handleSaveDates}>
                  Save
                </button>
                <button className="cancel-edit-button" onClick={handleCancelEdit}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h2>Welcome Message</h2>
        <div className="form-group">
          <label>Message shown to users on the registration page</label>
          <textarea
            value={settings.welcomeMessage}
            onChange={(e) => {
              setSettings(prev => ({
                ...prev,
                welcomeMessage: e.target.value
              }));
              setHasUnsavedChanges(true);
            }}
            placeholder="Welcome to Berwick Camp Registration!"
            rows={5}
          />
        </div>
      </div>

      <div className="settings-section">
        <h2>Registration Fees</h2>
        <div className="rates-table">
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th>Full Camp</th>
                <th>Nightly</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Adult</td>
                <td>
                  <input
                    type="text"
                    value={editingField === 'registration-adult-null' 
                      ? editValue 
                      : formatCurrency(settings.rates.registration.adult.weekly)}
                    onChange={(e) => handleWeeklyRateChange('registration', 'adult', null, e.target.value)}
                    onFocus={() => handleFocus('registration', 'adult', null, settings.rates.registration.adult.weekly)}
                    onBlur={() => handleBlur('registration', 'adult', null)}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={formatCurrency(settings.rates.registration.adult.nightly)}
                    disabled
                  />
                </td>
              </tr>
              <tr>
                <td>Adult Student</td>
                <td>
                  <input
                    type="text"
                    value={editingField === 'registration-adultStudent-null'
                      ? editValue
                      : formatCurrency(settings.rates.registration.adultStudent.weekly)}
                    onChange={(e) => handleWeeklyRateChange('registration', 'adultStudent', null, e.target.value)}
                    onFocus={() => handleFocus('registration', 'adultStudent', null, settings.rates.registration.adultStudent.weekly)}
                    onBlur={() => handleBlur('registration', 'adultStudent', null)}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={formatCurrency(settings.rates.registration.adultStudent.nightly)}
                    disabled
                  />
                </td>
              </tr>
              <tr>
                <td>Youth and Children</td>
                <td>
                  <input
                    type="text"
                    value={editingField === 'registration-youthAndChildren-null'
                      ? editValue
                      : formatCurrency(settings.rates.registration.youthAndChildren.weekly)}
                    onChange={(e) => handleWeeklyRateChange('registration', 'youthAndChildren', null, e.target.value)}
                    onFocus={() => handleFocus('registration', 'youthAndChildren', null, settings.rates.registration.youthAndChildren.weekly)}
                    onBlur={() => handleBlur('registration', 'youthAndChildren', null)}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={formatCurrency(settings.rates.registration.youthAndChildren.nightly)}
                    disabled
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="settings-section">
        <h2>Meal Fees</h2>
        <div className="rates-table">
          <table>
            <thead>
              <tr>
                <th>Pass Type</th>
                <th>Price</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Full Camp Pass - Adult</td>
                <td>
                  <input
                    type="text"
                    value={editingField === 'meals-fullCampAdult'
                      ? editValue
                      : formatCurrency(settings.rates.meals.fullCampAdult)}
                    onChange={(e) => {
                      const value = parseFloat(stripCurrency(e.target.value)) || 0;
                      setSettings(prev => ({
                        ...prev,
                        rates: {
                          ...prev.rates,
                          meals: { ...prev.rates.meals, fullCampAdult: value }
                        }
                      }));
                      setEditValue(e.target.value);
                      setHasUnsavedChanges(true);
                    }}
                    onFocus={() => {
                      setEditingField('meals-fullCampAdult');
                      setEditValue(settings.rates.meals.fullCampAdult.toString());
                    }}
                    onBlur={() => setEditingField(null)}
                  />
                </td>
              </tr>
              <tr>
                <td>Full Camp Pass - Child</td>
                <td>
                  <input
                    type="text"
                    value={editingField === 'meals-fullCampChild'
                      ? editValue
                      : formatCurrency(settings.rates.meals.fullCampChild)}
                    onChange={(e) => {
                      const value = parseFloat(stripCurrency(e.target.value)) || 0;
                      setSettings(prev => ({
                        ...prev,
                        rates: {
                          ...prev.rates,
                          meals: { ...prev.rates.meals, fullCampChild: value }
                        }
                      }));
                      setEditValue(e.target.value);
                      setHasUnsavedChanges(true);
                    }}
                    onFocus={() => {
                      setEditingField('meals-fullCampChild');
                      setEditValue(settings.rates.meals.fullCampChild.toString());
                    }}
                    onBlur={() => setEditingField(null)}
                  />
                </td>
              </tr>
              <tr>
                <td>Daily Pass - Adult</td>
                <td>
                  <input
                    type="text"
                    value={editingField === 'meals-dailyAdult'
                      ? editValue
                      : formatCurrency(settings.rates.meals.dailyAdult)}
                    onChange={(e) => {
                      const value = parseFloat(stripCurrency(e.target.value)) || 0;
                      setSettings(prev => ({
                        ...prev,
                        rates: {
                          ...prev.rates,
                          meals: { ...prev.rates.meals, dailyAdult: value }
                        }
                      }));
                      setEditValue(e.target.value);
                      setHasUnsavedChanges(true);
                    }}
                    onFocus={() => {
                      setEditingField('meals-dailyAdult');
                      setEditValue(settings.rates.meals.dailyAdult.toString());
                    }}
                    onBlur={() => setEditingField(null)}
                  />
                </td>
              </tr>
              <tr>
                <td>Daily Pass - Child</td>
                <td>
                  <input
                    type="text"
                    value={editingField === 'meals-dailyChild'
                      ? editValue
                      : formatCurrency(settings.rates.meals.dailyChild)}
                    onChange={(e) => {
                      const value = parseFloat(stripCurrency(e.target.value)) || 0;
                      setSettings(prev => ({
                        ...prev,
                        rates: {
                          ...prev.rates,
                          meals: { ...prev.rates.meals, dailyChild: value }
                        }
                      }));
                      setEditValue(e.target.value);
                      setHasUnsavedChanges(true);
                    }}
                    onFocus={() => {
                      setEditingField('meals-dailyChild');
                      setEditValue(settings.rates.meals.dailyChild.toString());
                    }}
                    onBlur={() => setEditingField(null)}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="settings-section">
        <h2>Accommodation Fees</h2>
        <div className="rates-table">
          <table>
            <thead>
              <tr>
                <th>Unit</th>
                <th>Full Camp</th>
                <th>Nightly</th>
              </tr>
            </thead>
            <tbody>
              {/* Cottages */}
              {getSortedAccommodationKeys('cottages', Object.keys(settings.rates.accommodation.cottages)).map((unit) => (
                <tr key={unit}>
                  <td>{getAccommodationDescription('cottages', unit)}</td>
                  <td>
                    <input
                      type="text"
                      value={editingField === `accommodation-cottages-${unit}`
                        ? editValue
                        : formatCurrency(settings.rates.accommodation.cottages[unit as keyof CottageRates].weekly)}
                      onChange={(e) => handleWeeklyRateChange('accommodation', 'cottages', unit, e.target.value)}
                      onFocus={() => handleFocus('accommodation', 'cottages', unit, settings.rates.accommodation.cottages[unit as keyof CottageRates].weekly)}
                      onBlur={() => handleBlur('accommodation', 'cottages', unit)}
                    />
                  </td>
                  <td>
                    {unit.startsWith('P') ? (
                      <div className="full-camp-only">Full Camp Rate Only</div>
                    ) : (
                      <input
                        type="text"
                        value={formatCurrency(settings.rates.accommodation.cottages[unit as keyof CottageRates].nightly)}
                        disabled
                      />
                    )}
                  </td>
                </tr>
              ))}

              {/* Apartments */}
              {getSortedAccommodationKeys('apartments', Object.keys(settings.rates.accommodation.apartments)).map((unit) => (
                <tr key={unit}>
                  <td>{getAccommodationDescription('apartments', unit)}</td>
                  <td>
                    <input
                      type="text"
                      value={editingField === `accommodation-apartments-${unit}`
                        ? editValue
                        : formatCurrency(settings.rates.accommodation.apartments[unit as keyof ApartmentRates].weekly)}
                      onChange={(e) => handleWeeklyRateChange('accommodation', 'apartments', unit, e.target.value)}
                      onFocus={() => handleFocus('accommodation', 'apartments', unit, settings.rates.accommodation.apartments[unit as keyof ApartmentRates].weekly)}
                      onBlur={() => handleBlur('accommodation', 'apartments', unit)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={formatCurrency(settings.rates.accommodation.apartments[unit as keyof ApartmentRates].nightly)}
                      disabled
                    />
                  </td>
                </tr>
              ))}

              {/* Dorm */}
              {Object.keys(settings.rates.accommodation.dorm).map((unit) => (
                <tr key={unit}>
                  <td>{getAccommodationDescription('dorm', unit)}</td>
                  <td>
                    <input
                      type="text"
                      value={editingField === `accommodation-dorm-${unit}`
                        ? editValue
                        : formatCurrency(settings.rates.accommodation.dorm[unit as keyof DormRates].weekly)}
                      onChange={(e) => handleWeeklyRateChange('accommodation', 'dorm', unit, e.target.value)}
                      onFocus={() => handleFocus('accommodation', 'dorm', unit, settings.rates.accommodation.dorm[unit as keyof DormRates].weekly)}
                      onBlur={() => handleBlur('accommodation', 'dorm', unit)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={formatCurrency(settings.rates.accommodation.dorm[unit as keyof DormRates].nightly)}
                      disabled
                    />
                  </td>
                </tr>
              ))}

              {/* Campsite */}
              {Object.keys(settings.rates.accommodation.campsite).map((unit) => (
                <tr key={unit}>
                  <td>{getAccommodationDescription('campsite', unit)}</td>
                  <td>
                    <input
                      type="text"
                      value={editingField === `accommodation-campsite-${unit}`
                        ? editValue
                        : formatCurrency(settings.rates.accommodation.campsite[unit as keyof CampsiteRates].weekly)}
                      onChange={(e) => handleWeeklyRateChange('accommodation', 'campsite', unit, e.target.value)}
                      onFocus={() => handleFocus('accommodation', 'campsite', unit, settings.rates.accommodation.campsite[unit as keyof CampsiteRates].weekly)}
                      onBlur={() => handleBlur('accommodation', 'campsite', unit)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={formatCurrency(settings.rates.accommodation.campsite[unit as keyof CampsiteRates].nightly)}
                      disabled
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="settings-section">
        <h2>Email Settings (MailerSend)</h2>
        <div className="form-row">
          <div className="form-group">
            <label>From Email</label>
            <input
              type="text"
              value={settings.mailerSend.fromEmail}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                mailerSend: {
                  ...prev.mailerSend,
                  fromEmail: e.target.value
                }
              }))}
              placeholder="registraruccma@gmail.com"
            />
          </div>
          <div className="form-group">
            <label>From Name</label>
            <input
              type="text"
              value={settings.mailerSend.fromName}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                mailerSend: {
                  ...prev.mailerSend,
                  fromName: e.target.value
                }
              }))}
              placeholder="Berwick Camp"
            />
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.mailerSend.enabled}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  mailerSend: {
                    ...prev.mailerSend,
                    enabled: e.target.checked
                  }
                }))}
              />
              Enable Email Notifications
            </label>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Test Email Address</label>
            <div className="input-with-button">
              <input
                type="text"
                value={testEmailAddress}
                onChange={(e) => setTestEmailAddress(e.target.value)}
                placeholder="Enter email address for testing"
              />
              <button 
                className="test-email-button"
                onClick={handleTestEmail}
                disabled={sendingTestEmail || !testEmailAddress}
              >
                {sendingTestEmail ? 'Sending...' : 'Send Test Email'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h2>Confirmation Email Program Paragraphs</h2>
        <div className="settings-info">
          <p>
            Add optional program-specific paragraphs that can be inserted into the confirmation email.
            Sections appear in this order when applicable: Nursery, VBC (P-8), Senior Youth (9-12).
          </p>
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.confirmationEmailProgramParagraphs.nursery.enabled}
              onChange={(e) => {
                setSettings(prev => ({
                  ...prev,
                  confirmationEmailProgramParagraphs: {
                    ...prev.confirmationEmailProgramParagraphs,
                    nursery: {
                      ...prev.confirmationEmailProgramParagraphs.nursery,
                      enabled: e.target.checked
                    }
                  }
                }));
                setHasUnsavedChanges(true);
              }}
            />
            Enable Nursery Paragraph
          </label>
          <label>Nursery paragraph text (shown when at least one Nursery camper attends)</label>
          <textarea
            value={settings.confirmationEmailProgramParagraphs.nursery.text}
            onChange={(e) => {
              setSettings(prev => ({
                ...prev,
                confirmationEmailProgramParagraphs: {
                  ...prev.confirmationEmailProgramParagraphs,
                  nursery: {
                    ...prev.confirmationEmailProgramParagraphs.nursery,
                    text: e.target.value
                  }
                }
              }));
              setHasUnsavedChanges(true);
            }}
            rows={4}
            placeholder="Enter Nursery program details and links."
          />
          {settings.confirmationEmailProgramParagraphs.nursery.text.trim() && (
            <div className="paragraph-preview">
              <strong>Preview:</strong> {renderParagraphPreviewWithLinks(settings.confirmationEmailProgramParagraphs.nursery.text)}
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.confirmationEmailProgramParagraphs.vbc.enabled}
              onChange={(e) => {
                setSettings(prev => ({
                  ...prev,
                  confirmationEmailProgramParagraphs: {
                    ...prev.confirmationEmailProgramParagraphs,
                    vbc: {
                      ...prev.confirmationEmailProgramParagraphs.vbc,
                      enabled: e.target.checked
                    }
                  }
                }));
                setHasUnsavedChanges(true);
              }}
            />
            Enable VBC Paragraph
          </label>
          <label>VBC paragraph text (shown when at least one Grade P-5 or Grade 6-8 camper attends)</label>
          <textarea
            value={settings.confirmationEmailProgramParagraphs.vbc.text}
            onChange={(e) => {
              setSettings(prev => ({
                ...prev,
                confirmationEmailProgramParagraphs: {
                  ...prev.confirmationEmailProgramParagraphs,
                  vbc: {
                    ...prev.confirmationEmailProgramParagraphs.vbc,
                    text: e.target.value
                  }
                }
              }));
              setHasUnsavedChanges(true);
            }}
            rows={4}
            placeholder="Enter VBC program details and links."
          />
          {settings.confirmationEmailProgramParagraphs.vbc.text.trim() && (
            <div className="paragraph-preview">
              <strong>Preview:</strong> {renderParagraphPreviewWithLinks(settings.confirmationEmailProgramParagraphs.vbc.text)}
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.confirmationEmailProgramParagraphs.seniorYouth.enabled}
              onChange={(e) => {
                setSettings(prev => ({
                  ...prev,
                  confirmationEmailProgramParagraphs: {
                    ...prev.confirmationEmailProgramParagraphs,
                    seniorYouth: {
                      ...prev.confirmationEmailProgramParagraphs.seniorYouth,
                      enabled: e.target.checked
                    }
                  }
                }));
                setHasUnsavedChanges(true);
              }}
            />
            Enable Senior Youth Paragraph
          </label>
          <label>Senior Youth paragraph text (shown when at least one Grade 9-12 camper attends)</label>
          <textarea
            value={settings.confirmationEmailProgramParagraphs.seniorYouth.text}
            onChange={(e) => {
              setSettings(prev => ({
                ...prev,
                confirmationEmailProgramParagraphs: {
                  ...prev.confirmationEmailProgramParagraphs,
                  seniorYouth: {
                    ...prev.confirmationEmailProgramParagraphs.seniorYouth,
                    text: e.target.value
                  }
                }
              }));
              setHasUnsavedChanges(true);
            }}
            rows={4}
            placeholder="Enter Senior Youth program details and links."
          />
          {settings.confirmationEmailProgramParagraphs.seniorYouth.text.trim() && (
            <div className="paragraph-preview">
              <strong>Preview:</strong> {renderParagraphPreviewWithLinks(settings.confirmationEmailProgramParagraphs.seniorYouth.text)}
            </div>
          )}
        </div>
      </div>

      <div className="settings-section">
        <h2>User Management</h2>
        <UserManagement />
      </div>

      {/* Prepare for New Year */}
      <PrepareForNewYear />

      <div className="floating-action-bar">
        <button 
          className="floating-save-button"
          onClick={handleSave}
          disabled={!hasUnsavedChanges}
        >
          Save Changes
        </button>
        {success && (
          <div className="save-status success">Settings saved successfully</div>
        )}
      </div>
    </div>
  );
};

export default AdminSettings; 